import { useEffect, useMemo, useState } from 'react';
import {
  powerPointShapeCatalog,
  powerPointLineTools,
  markStampShapeTypes,
  type PowerPointShapeGroup,
} from '@pdf-ticket/editor';
import { useEditorStore } from '../lib/editor-store.js';
import { shapeSvgPathD, isShapePathReady, isMarkShape, isFilledMark } from '../lib/shape-svg.js';

/*
 * PowerPoint-style shape gallery. Reproduces the "図形" insertion panel: every
 * catalog category (lines / rectangles / basic / block arrows / equation /
 * flowchart / stars & banners / callouts / action buttons) shown as a grid of
 * silhouette icons. Path-ready shapes are clickable and arm the placement tool;
 * not-yet-implemented prst are shown dimmed ("近日対応") so the screen still
 * mirrors PowerPoint without pretending unsupported shapes will draw.
 */

const GROUP_JP: Record<PowerPointShapeGroup, string> = {
  lines: '線',
  rectangles: '四角形',
  basic: '基本図形',
  blockArrows: 'ブロック矢印',
  equation: '数式図形',
  flowchart: 'フローチャート',
  starsAndBanners: '星とリボン',
  callouts: '吹き出し',
  actionButtons: '動作設定ボタン',
  legacy: 'その他',
};

// Japanese names for the common shapes; anything not listed falls back to a
// humanized version of the OOXML prst id.
const SHAPE_JP: Record<string, string> = {
  Rectangle: '正方形/長方形',
  RoundRectangle: '角丸四角形',
  Round1Rectangle: '1つの角を丸めた四角形',
  Round2SameRectangle: '同じ側の2つの角を丸めた四角形',
  Round2DiagonalRectangle: '対角の2つの角を丸めた四角形',
  SnipRoundRectangle: '1つの角を切り取り・1つを丸めた四角形',
  Snip1Rectangle: '1つの角を切り取った四角形',
  Snip2SameRectangle: '同じ側の2つの角を切り取った四角形',
  Snip2DiagonalRectangle: '対角の2つの角を切り取った四角形',
  Bevel: 'ベベル',
  Frame: '額縁',
  HalfFrame: '四半円の額縁',
  Triangle: '二等辺三角形',
  RightTriangle: '直角三角形',
  Diamond: 'ひし形',
  Parallelogram: '平行四辺形',
  Trapezoid: '台形',
  NonIsoscelesTrapezoid: '不等辺台形',
  Pentagon: '五角形',
  Hexagon: '六角形',
  Heptagon: '七角形',
  Octagon: '八角形',
  Decagon: '十角形',
  Dodecagon: '十二角形',
  Ellipse: '楕円',
  Pie: '部分円（パイ）',
  Chord: '弦',
  Arc: '円弧',
  BlockArc: 'ブロックアーク',
  Donut: 'ドーナツ',
  Heart: 'ハート',
  LightningBolt: '稲妻',
  Sun: '太陽',
  Moon: '月',
  Cloud: '雲',
  SmileyFace: 'スマイル顔',
  NoSmoking: '禁止マーク',
  Plus: '十字',
  Cube: '直方体',
  Can: '円柱',
  Plaque: 'プラーク',
  Teardrop: '涙形',
  HomePlate: '五角形矢印（ホームベース）',
  Chevron: 'V字矢印（シェブロン）',
  RightArrow: '右矢印',
  LeftArrow: '左矢印',
  UpArrow: '上矢印',
  DownArrow: '下矢印',
  LeftRightArrow: '左右矢印',
  UpDownArrow: '上下矢印',
  QuadArrow: '四方向矢印',
  NotchedRightArrow: '凹型矢印',
  StripedRightArrow: 'ストライプ右矢印',
  BentArrow: '折れ曲がり矢印',
  UturnArrow: 'Uターン矢印',
  MathPlus: '加算',
  MathMinus: '減算',
  MathMultiply: '乗算',
  MathDivide: '除算',
  MathEqual: '等号',
  MathNotEqual: '不等号',
  Star4: '4pt星',
  Star5: '5pt星',
  Star6: '6pt星',
  Star7: '7pt星',
  Star8: '8pt星',
  Star10: '10pt星',
  Star12: '12pt星',
  Star16: '16pt星',
  Star24: '24pt星',
  Star32: '32pt星',
  IrregularSeal1: '爆発1',
  IrregularSeal2: '爆発2',
  Ribbon: '下リボン',
  Ribbon2: '上リボン',
  VerticalScroll: '縦巻き巻物',
  HorizontalScroll: '横巻き巻物',
  Wave: '波',
  DoubleWave: '二重波',
  FlowChartProcess: '処理',
  FlowChartAlternateProcess: '代替処理',
  FlowChartDecision: '判断',
  FlowChartInputOutput: 'データ',
  FlowChartPredefinedProcess: '定義済み処理',
  FlowChartInternalStorage: '内部記憶',
  FlowChartDocument: '書類',
  FlowChartMultidocument: '複数書類',
  FlowChartTerminator: '端子',
  FlowChartPreparation: '準備',
  FlowChartManualInput: '手操作入力',
  FlowChartManualOperation: '手作業',
  FlowChartConnector: '結合子',
  FlowChartOffpageConnector: 'ページ外結合子',
  FlowChartPunchedCard: 'カード',
  FlowChartExtract: '抽出',
  FlowChartMerge: '組み合わせ',
  FlowChartSort: '整列',
  WedgeRectCallout: '四角形吹き出し',
  WedgeRRectCallout: '角丸四角形吹き出し',
  WedgeEllipseCallout: '楕円形吹き出し',
  CloudCallout: '雲形吹き出し',
  ActionButtonBlank: '空白ボタン',
  ActionButtonHome: 'ホーム',
  ActionButtonForwardNext: '進む/次へ',
  ActionButtonBackPrevious: '戻る/前へ',
  MarkCheck: 'チェックマーク',
  MarkCross: 'バツ印',
  MarkCircle: '丸（白丸）',
  MarkDot: '丸（黒丸）',
  MarkDoubleCircle: '二重丸',
};

function humanize(prst: string): string {
  return prst
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/^Flow Chart /, '')
    .trim();
}

function labelFor(shapeType: string): string {
  return SHAPE_JP[shapeType] ?? humanize(shapeType);
}

// Straight line tools that are placeable today (2-point drag). Elbow/curve/
// freeform/scribble remain "近日対応".
const LINE_TOOLS_READY = new Set<string>([
  'line',
  'lineArrow',
  'lineDoubleArrow',
  'straightConnector',
  'straightArrowConnector',
  'straightDoubleArrowConnector',
]);

/** Mini icon for a line tool: a diagonal with arrowheads per the tool id. */
function LineIcon({ toolId }: { toolId: string }) {
  const dbl = /Double/.test(toolId);
  const endA = /Arrow/.test(toolId);
  return (
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <line x1="18" y1="82" x2="82" y2="18" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      {endA && <polygon points="82,18 64,26 74,36" fill="currentColor" />}
      {dbl && <polygon points="18,82 26,64 36,74" fill="currentColor" />}
    </svg>
  );
}

function LineCell({ toolId, label, onPick }: { toolId: string; label: string; onPick: (id: string) => void }) {
  if (!LINE_TOOLS_READY.has(toolId)) {
    return (
      <span className="shape-cell shape-cell--disabled" title={`${label}（近日対応）`} aria-disabled="true">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <line x1="16" y1="84" x2="84" y2="16" />
        </svg>
      </span>
    );
  }
  return (
    <button type="button" className="shape-cell shape-cell--line" title={label} aria-label={label} onClick={() => onPick(toolId)}>
      <LineIcon toolId={toolId} />
    </button>
  );
}

function ShapeCell({
  shapeType,
  onPick,
}: {
  shapeType: string;
  onPick: (shapeType: string) => void;
}) {
  const ready = isShapePathReady(shapeType);
  const d = ready ? shapeSvgPathD(shapeType, 100) : null;
  const label = labelFor(shapeType);
  // Outline marks (✓ ✗ ○ ◎) render as stroked line-art; ● and autoshapes fill.
  const markOutline = isMarkShape(shapeType) && !isFilledMark(shapeType);
  if (!ready || !d) {
    return (
      <span className="shape-cell shape-cell--disabled" title={`${label}（近日対応）`} aria-disabled="true">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <rect x="14" y="14" width="72" height="72" rx="8" />
        </svg>
      </span>
    );
  }
  return (
    <button
      type="button"
      className={markOutline ? 'shape-cell shape-cell--mark' : 'shape-cell'}
      title={label}
      aria-label={label}
      onClick={() => onPick(shapeType)}
    >
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <path d={d} />
      </svg>
    </button>
  );
}

export function ShapeGallery({
  open,
  onClose,
  recent,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  recent: ReadonlyArray<string>;
  onPick: (shapeType: string) => void;
}) {
  const setActiveShape = useEditorStore((s) => s.setActiveShape);
  const setActiveLineTool = useEditorStore((s) => s.setActiveLineTool);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Reset the filter whenever the gallery reopens.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const q = query.trim().toLowerCase();
  const matches = (shapeType: string) =>
    q === '' ||
    shapeType.toLowerCase().includes(q) ||
    labelFor(shapeType).toLowerCase().includes(q);

  const groups = useMemo(
    () =>
      powerPointShapeCatalog
        .filter((g) => g.group !== 'legacy')
        .map((g) => ({ ...g, shapeTypes: g.shapeTypes.filter(matches) }))
        .filter((g) => g.shapeTypes.length > 0),
    [q],
  );
  const lineMatches = powerPointLineTools.filter((t) => matches(t.id) || matches(t.label));
  const markMatches = markStampShapeTypes.filter(matches);
  const recentMatches = recent.filter(matches);

  const pick = (shapeType: string) => {
    setActiveShape(shapeType);
    onPick(shapeType);
    onClose();
  };
  const pickLine = (toolId: string) => {
    setActiveLineTool(toolId);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="shape-backdrop" onClick={onClose} role="presentation">
      <div
        className="shape-modal"
        role="dialog"
        aria-modal="true"
        aria-label="図形の挿入"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shape-modal__head">
          <h2>図形</h2>
          <input
            className="shape-search"
            type="search"
            autoFocus
            placeholder="図形を検索 (例: 矢印, star, 吹き出し)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div className="shape-scroll">
          {recentMatches.length > 0 && (
            <section className="shape-group">
              <h3>最近使った図形</h3>
              <div className="shape-grid">
                {recentMatches.map((s) => (
                  <ShapeCell key={`recent-${s}`} shapeType={s} onPick={pick} />
                ))}
              </div>
            </section>
          )}

          {lineMatches.length > 0 && (
            <section className="shape-group">
              <h3>{GROUP_JP.lines}</h3>
              <div className="shape-grid">
                {lineMatches.map((t) => (
                  <LineCell key={`line-${t.id}`} toolId={t.id} label={t.label} onPick={pickLine} />
                ))}
              </div>
            </section>
          )}

          {markMatches.length > 0 && (
            <section className="shape-group">
              <h3>マーク</h3>
              <div className="shape-grid">
                {markMatches.map((s) => (
                  <ShapeCell key={`mark-${s}`} shapeType={s} onPick={pick} />
                ))}
              </div>
            </section>
          )}

          {groups.map((g) => (
            <section key={g.group} className="shape-group">
              <h3>{GROUP_JP[g.group]}</h3>
              <div className="shape-grid">
                {g.shapeTypes.map((s) => (
                  <ShapeCell key={s} shapeType={s} onPick={pick} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
